/**
 * useAuthAuditTrail — logs auth lifecycle events to scout_activity_log.
 *
 * Listens for CustomEvents dispatched by ServiceLayerAuthProvider:
 * - auth:re-handoff-attempt — SL cookies expired, trying to restore
 * - auth:re-handoff-success — cookies restored successfully
 * - auth:re-handoff-failed — restoration failed, user may see login screen
 * - auth:session-expired — session cleared, user is logged out
 * - service-layer:unauthorized — 401 received from API
 *
 * Each event is written to scout_activity_log via the existing
 * activity tracker infrastructure. Also captured by Sentry as breadcrumbs.
 */

import { useEffect } from 'react'
import { captureError } from '@appic/lib/utils/scoutErrorCapture'

const AUTH_EVENTS = [
  'auth:re-handoff-attempt',
  'auth:re-handoff-success',
  'auth:re-handoff-failed',
  'auth:session-expired',
  'service-layer:unauthorized',
] as const

/**
 * Send one audit event. Fire-and-forget for the CALLER (never awaited —
 * must not block the auth flow) but NOT fire-and-forget for the delivery
 * itself: every outcome (network reject, non-2xx, synchronous throw) is
 * recorded via captureError, which writes to scout_activity_log over the
 * independent direct-to-Supabase path (@appic/lib/utils/scoutLog) — so a
 * failure of THIS ingest route still produces a durable, queryable trace.
 *
 * Uses `fetch` with `keepalive: true` instead of `navigator.sendBeacon`:
 * sendBeacon returns only a boolean (queued vs refused) and gives no way to
 * see a non-2xx response from the ingest route itself (e.g. a 401 on the
 * auth-audit endpoint), whereas keepalive-fetch survives page teardown the
 * same way and returns a real Response to assert on.
 */
function sendAuditEvent(eventName: string, detail: unknown): void {
  const apiBase = import.meta.env.VITE_SERVICE_LAYER_BASE_URL || '/service'
  const route = window.location.pathname

  try {
    fetch(`${apiBase}/v1/scout/activity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: eventName, payload: detail, route }),
      keepalive: true,
    })
      .then((res) => {
        if (!res.ok) {
          captureError(new Error(`auth audit delivery rejected: HTTP ${res.status} ${res.statusText}`), {
            tags: { feature: 'auth-audit-trail', event: eventName },
            extra: { route, status: res.status },
          })
        }
      })
      .catch((err) => {
        captureError(err, {
          tags: { feature: 'auth-audit-trail', event: eventName },
          extra: { route },
        })
      })
  } catch (err) {
    // keepalive fetch can throw synchronously (e.g. payload over the 64KB
    // keepalive ceiling). Never block the auth flow — but record it, don't
    // swallow it.
    captureError(err, {
      tags: { feature: 'auth-audit-trail', event: eventName },
      extra: { route },
    })
  }
}

export function useAuthAuditTrail() {
  useEffect(() => {
    const handlers = AUTH_EVENTS.map((eventName) => {
      const handler = (e: Event) => {
        const detail = (e as CustomEvent).detail ?? {}

        // Log to console for Sentry breadcrumbs
        console.warn(`[AuthAudit] ${eventName}`, detail)

        sendAuditEvent(eventName, detail)
      }

      window.addEventListener(eventName, handler)
      return { eventName, handler }
    })

    return () => {
      handlers.forEach(({ eventName, handler }) => {
        window.removeEventListener(eventName, handler)
      })
    }
  }, [])
}
